<template>
  <footer class="relative bg-gradient-to-br from-dark-gray to-dark-gray/90 text-white overflow-hidden">
    <!-- Animated Top Border -->
    <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-light-purple via-purple-400 to-light-purple">
      <div
        class="h-full bg-gradient-to-r from-transparent via-white to-transparent"
        v-motion
        :enter="{ x: ['-100%', '100%'] }"
        :duration="3000"
        :repeat="Infinity"
      />
    </div>

    <!-- Background Pattern -->
    <div class="absolute inset-0 opacity-5">
      <div class="absolute inset-0" style="
        background-image: radial-gradient(circle at 2px 2px, white 1px, transparent 0);
        background-size: 40px 40px;
      " />
    </div>

    <div class="container mx-auto px-6 py-16 relative z-10">
      <div class="grid lg:grid-cols-2 md:grid-cols-2 gap-8 mb-12">
        <!-- Brand Section -->
        <div
          v-motion
          :initial="{ opacity: 0, y: 30 }"
          :visible-once="{ opacity: 1, y: 0 }"
          :duration="600"
        >
          <div class="mb-6">
            <h3 class="text-3xl font-poppins font-bold text-light-purple mb-2">
              Homvisit
            </h3>
            <p class="text-lg text-gray-300 font-medium">
              Healthcare Beyond Boundaries.
            </p>
          </div>
          
          <p class="text-gray-400 leading-relaxed mb-6">
            Corporate health camps, onsite phlebotomy teams, and an always-open phlebotomist network across Kerala.
            Home collection available as a secondary service.
          </p>
          <div class="flex flex-wrap gap-3 mb-6 text-sm">
            <RouterLink to="/services" class="text-light-purple hover:underline">Services</RouterLink>
            <RouterLink to="/services/corporate-health-camps" class="text-light-purple hover:underline">Corporate camps</RouterLink>
            <RouterLink to="/insights" class="text-light-purple hover:underline">Insights</RouterLink>
            <RouterLink to="/resources" class="text-light-purple hover:underline">Resources</RouterLink>
            <RouterLink to="/jobs" class="text-light-purple hover:underline">Phlebo jobs</RouterLink>
          </div>

          <!-- Social Links -->
          <div class="flex space-x-4">
            <a
              v-for="(social, index) in socialLinks"
              :key="index"
              :href="social.href"
              class="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-light-purple transition-all duration-300 cursor-pointer"
              :aria-label="social.label"
              @mouseenter="onSocialHover"
              @mouseleave="onSocialLeave"
            >
              <component :is="social.icon" :size="18" />
            </a>
          </div>
        </div>


        <!-- Contact Info -->
        <div
          v-motion
          :initial="{ opacity: 0, y: 30 }"
          :visible-once="{ opacity: 1, y: 0 }"
          :duration="600"
          :delay="300"
        >
          <h4 class="text-xl font-poppins font-semibold mb-6 text-light-purple">
            Contact Info
          </h4>
          <div class="space-y-4">
            <div class="flex items-start space-x-3">
              <Phone :size="18" class="text-light-purple mt-1 flex-shrink-0" />
              <div>
                <p class="text-white font-medium">75 9409 9409</p>
                <p class="text-gray-400 text-sm">Mon-Sat 9:00 AM - 6:00 PM</p>
              </div>
            </div>
            
            <div class="flex items-start space-x-3">
              <Mail :size="18" class="text-light-purple mt-1 flex-shrink-0" />
              <div>
                <p class="text-white font-medium">sujithvijayan@homvisit.com</p>
                <p class="text-gray-400 text-sm">We reply within 24 hours</p>
              </div>
            </div>
            
            <div class="flex items-start space-x-3">
              <MapPin :size="18" class="text-light-purple mt-1 flex-shrink-0" />
              <div>
                <p class="text-white font-medium">Homvisit</p>
                <p class="text-gray-400 text-sm">Trivandrum, Kerala</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom Section -->
      <div
        class="border-t border-gray-700 pt-8"
        v-motion
        :initial="{ opacity: 0 }"
        :visible-once="{ opacity: 1 }"
        :duration="600"
        :delay="400"
      >
        <div class="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div class="flex items-center space-x-2 text-gray-400">
            <span>© {{ currentYear }} Homvisit. All rights reserved.</span>
            <Heart :size="16" class="text-light-purple" />
          </div>
          
          <div class="flex flex-wrap items-center justify-center md:justify-end gap-x-6 gap-y-2 text-sm text-gray-400">
            <RouterLink
              v-for="(policy, index) in policyLinks"
              :key="index"
              :to="policy.to"
              class="hover:text-light-purple transition-colors duration-300 cursor-pointer"
              @mouseenter="onPolicyHover"
              @mouseleave="onPolicyLeave"
            >
              {{ policy.label }}
            </RouterLink>
          </div>
        </div>
      </div>
    </div>

    <!-- Floating Chat Button -->
    <div
      class="fixed bottom-6 right-6 z-50"
      v-motion
      :initial="{ scale: 0, opacity: 0 }"
      :enter="{ scale: 1, opacity: 1 }"
      :duration="500"
      :delay="1000"
    >
      <a
        href="https://wa.me/917594099409"
        target="_blank"
        rel="noopener"
        class="w-14 h-14 bg-light-purple rounded-full shadow-lg flex items-center justify-center text-white hover:shadow-glow cursor-pointer"
        @mouseenter="onChatHover"
        @mouseleave="onChatLeave"
        v-motion
        :enter="{ 
          boxShadow: [
            '0 4px 20px rgba(166, 140, 241, 0.3)',
            '0 4px 30px rgba(166, 140, 241, 0.5)',
            '0 4px 20px rgba(166, 140, 241, 0.3)'
          ]
        }"
        :duration="2000"
        :repeat="Infinity"
        aria-label="Chat on WhatsApp"
      >
        <Mail :size="20" />
      </a>
    </div>
  </footer>
</template>

<script>
import { computed } from 'vue'
import { Heart, Linkedin, Mail, Phone, MapPin } from 'lucide-vue-next'

export default {
  name: 'FooterSection',
  components: {
    Heart,
    Linkedin,
    Mail,
    Phone,
    MapPin
  },
  setup() {
    const currentYear = computed(() => new Date().getFullYear())

    const socialLinks = [
      { icon: Linkedin, href: '#', label: 'LinkedIn' },
      { icon: Mail, href: 'mailto:sujithvijayan@homvisit.com', label: 'Email' },
    ]

    const policyLinks = [
      { label: 'Privacy Policy', to: { name: 'privacy-policy' } },
      { label: 'LinkedIn Privacy', to: { name: 'linkedin-privacy-policy' } },
      { label: 'Terms of Service', to: { name: 'terms-of-service' } },
      { label: 'Cookie Policy', to: { name: 'cookie-policy' } }
    ]

    const onSocialHover = (e) => {
      e.target.style.transform = 'scale(1.1) translateY(-2px)'
    }

    const onSocialLeave = (e) => {
      e.target.style.transform = 'scale(1) translateY(0px)'
    }

    const onPolicyHover = (e) => {
      e.target.style.transform = 'translateY(-2px)'
    }

    const onPolicyLeave = (e) => {
      e.target.style.transform = 'translateY(0px)'
    }

    const onChatHover = (e) => {
      e.target.style.transform = 'scale(1.1) translateY(-2px)'
    }

    const onChatLeave = (e) => {
      e.target.style.transform = 'scale(1) translateY(0px)'
    }

    return {
      currentYear,
      socialLinks,
      policyLinks,
      onSocialHover,
      onSocialLeave,
      onPolicyHover,
      onPolicyLeave,
      onChatHover,
      onChatLeave
    }
  }
}
</script>
